import { AUTH0_CONFIG, REDIRECT_IGNORE_PATHS } from '@/constants';
import Vue from 'vue';
import store from '@/store/store';
import auth0 from 'auth0-js';
import axios from 'axios';

const authDemoStorageKey = 'authDemo';
const authDemoStorage = localStorage.getItem(authDemoStorageKey)
    ? JSON.parse(localStorage.getItem(authDemoStorageKey) || '')
    : {};

const webAuth = new auth0.WebAuth({
    audience: AUTH0_CONFIG.audience,
    clientID: AUTH0_CONFIG.clientID,
    domain: AUTH0_CONFIG.domain,
    responseType: AUTH0_CONFIG.responseType,
    redirectUri: AUTH0_CONFIG.redirectUri,
    scope: AUTH0_CONFIG.scope
});

const auth = new Vue({
    data: {
        renewTokensTimeout: null
    },
    computed: {
        accessToken() {
            return store.state.user.status.accessToken;
        },
        expiresAt() {
            return store.state.user.status.expiresAt;
        },
        idToken() {
            return store.state.user.status.idToken;
        },
        sub() {
            return store.state.user.status.sub;
        },
        userMetaKey() {
            return webAuth.baseOptions.rootUrl + '_user_metadata';
        },
        isAuthenticated() {
            return this.accessToken && this.expiresAt > new Date().getTime();
        }
    },

    methods: {
        login(params) {
            return new Promise((resolve, reject) => {
                // チュートリアルを表示するフラグを立てる
                store.dispatch('page/setStatus', {
                    page: 'tutorial',
                    status: {
                        isOpenModal: true
                    }
                });

                // デモモードの場合はダッシュボードでデモ用の案内を表示する
                if (
                    process.env.VUE_APP_ENV === 'local' ||
                    process.env.VUE_APP_ENV === 'demo'
                ) {
                    const isDemoUser =
                        params.email === process.env.VUE_APP_DEMO_USER &&
                        params.password === process.env.VUE_APP_DEMO_PASSWORD;

                    if (isDemoUser) {
                        this.resetAll();
                        localStorage.setItem(
                            authDemoStorageKey,
                            JSON.stringify({
                                password: process.env.VUE_APP_DEMO_PASSWORD
                            })
                        );
                        store.dispatch('demo/setStatus', {
                            isOpenModal: process.env.VUE_APP_ENV === 'demo'
                        });
                        location.href = process.env.BASE_URL + 'projects';
                    } else {
                        reject({ code: 'access_denied' });
                    }
                    return;
                }

                webAuth.redirect.loginWithCredentials(
                    {
                        connection: AUTH0_CONFIG.connection,
                        redirectUri: AUTH0_CONFIG.redirectUri,
                        username: params.email,
                        password: params.password
                    },
                    error => {
                        if (error) {
                            reject(error);
                        } else {
                            this.resetAll();
                            resolve();
                        }
                    }
                );
            });
        },

        logout(returnTo = AUTH0_CONFIG.returnTo.default) {
            this.resetAll();
            // ローダー表示
            store.dispatch('view/setLoading', true);
            if (
                process.env.VUE_APP_ENV === 'local' ||
                process.env.VUE_APP_ENV === 'demo'
            ) {
                location.href = returnTo;
                return;
            }
            webAuth.logout({
                returnTo: returnTo
            });
        },

        signup(params, isTrial) {
            const metaData = {
                agentCode: params.agentCode,
                company: params.company,
                tel: params.tel,
                userkana: params.userkana,
                username: params.username
            };
            if (isTrial) {
                // signupメソッドではString型しか許可されていない
                // 気になるのでAuth0のHook（Pre User Registration）でBoolean型に変換する
                metaData.isTrial = 'true';
            }

            if (
                process.env.VUE_APP_ENV === 'local' ||
                process.env.VUE_APP_ENV === 'demo'
            ) {
                return axios.post(
                    `${process.env.VUE_APP_DEMO_AUTH_API_BASE_URL}/v1/dbconnections/signup`,
                    {
                        email: params.email,
                        password: params.password,
                        user_metadata: metaData
                    }
                );
            }

            return new Promise((resolve, reject) => {
                webAuth.signup(
                    {
                        connection: AUTH0_CONFIG.connection,
                        email: params.email,
                        password: params.password,
                        user_metadata: metaData
                    },
                    (error, response) => {
                        if (error) {
                            reject(error);
                        } else {
                            resolve();
                        }
                    }
                );
            });
        },

        parseHash() {
            // URLハッシュを解析する
            return new Promise((resolve, reject) => {
                webAuth.parseHash((error, response) => {
                    if (response && response.accessToken && response.idToken) {
                        this.setSession(response);
                        resolve(response);
                    } else if (error) {
                        reject(error);
                    } else {
                        reject();
                    }
                });
            });
        },

        setSession(response) {
            this.setUserStatus(response);
            this.scheduleRenewTokens();
        },

        // ユーザーの認証ステータスをチェックして、ログインセッションがまだ有効な場合はsetSessionを呼び出す
        renewTokens() {
            if (
                process.env.VUE_APP_ENV === 'local' ||
                process.env.VUE_APP_ENV === 'demo'
            ) {
                if (
                    authDemoStorage.password ===
                    process.env.VUE_APP_DEMO_PASSWORD
                ) {
                    this.setSession();
                    return Promise.resolve();
                } else {
                    return Promise.reject();
                }
            }
            return new Promise((resolve, reject) => {
                webAuth.checkSession({}, (error, response) => {
                    if (response && response.accessToken && response.idToken) {
                        this.setSession(response);
                        resolve(response);
                    } else if (error) {
                        reject(error);
                    }
                });
            });
        },

        resetAll() {
            clearTimeout(this.renewTokensTimeout);
            store.dispatch('logout');
            localStorage.removeItem(authDemoStorageKey);
        },

        setRedirectPath(path) {
            if (!path) return;
            // リダイレクト除外パス
            for (const regexp of REDIRECT_IGNORE_PATHS) {
                if (regexp.test(path)) return;
            }
            localStorage.setItem('redirectPath', path);
        },

        setUserStatus(response) {
            if (
                process.env.VUE_APP_ENV === 'local' ||
                process.env.VUE_APP_ENV === 'demo'
            ) {
                // デモユーザーの場合、デモ用のユーザー情報に変える
                if (
                    authDemoStorage.password ===
                    process.env.VUE_APP_DEMO_PASSWORD
                ) {
                    store.dispatch('user/setStatus', {
                        accessToken: 'demoToken',
                        email: process.env.VUE_APP_DEMO_USER,
                        emailVerified: true,
                        expiresAt: 4102412400000 * 1000 + new Date().getTime(),
                        idToken: 'demoToken',
                        sub: 'demoUser'
                    });
                }
                return;
            }

            store.dispatch('user/setStatus', {
                accessToken: response.accessToken,
                email: response.idTokenPayload.email,
                emailVerified: response.idTokenPayload.email_verified,
                expiresAt: response.expiresIn * 1000 + new Date().getTime(),
                idToken: response.idToken,
                sub: response.idTokenPayload.sub
            });
        },
        scheduleRenewTokens() {
            clearTimeout(this.renewTokensTimeout);
            let delay = this.expiresAt - Date.now();
            this.renewTokensTimeout = setTimeout(() => {
                this.renewTokens().catch(() => {
                    this.logout();
                });
            }, delay);
        },

        getUserData() {
            if (
                process.env.VUE_APP_ENV === 'local' ||
                process.env.VUE_APP_ENV === 'demo'
            ) {
                return axios.get(
                    `${process.env.VUE_APP_DEMO_AUTH_API_BASE_URL}/v1/users/${this.sub}`
                );
            }
            return axios.get(
                `${AUTH0_CONFIG.customAudience}users/${this.sub}`,
                {
                    headers: {
                        Authorization: 'Bearer ' + this.accessToken,
                        'content-type': 'application/json'
                    },
                    params: {
                        fields: 'email,email_verified,user_metadata',
                        scope: AUTH0_CONFIG.scope
                    }
                }
            );
        },

        updateUserData(data) {
            if (
                process.env.VUE_APP_ENV === 'local' ||
                process.env.VUE_APP_ENV === 'demo'
            ) {
                return axios.patch(
                    `${process.env.VUE_APP_DEMO_AUTH_API_BASE_URL}/v1/config/users/${this.sub}`,
                    data
                );
            }
            return axios({
                method: 'patch',
                url: `${AUTH0_CONFIG.customAudience}users/${this.sub}`,
                data: data,
                headers: {
                    Authorization: 'Bearer ' + this.accessToken,
                    'content-type': 'application/json'
                }
            });
        },

        changePassword(params) {
            if (
                process.env.VUE_APP_ENV === 'local' ||
                process.env.VUE_APP_ENV === 'demo'
            ) {
                return axios.post(
                    `${process.env.VUE_APP_DEMO_AUTH_API_BASE_URL}/v1/dbconnections/change_password`,
                    params
                );
            }
            return new Promise((resolve, reject) => {
                webAuth.changePassword(
                    {
                        connection: AUTH0_CONFIG.connection,
                        email: params.email
                    },
                    (error, response) => {
                        if (error) {
                            reject(error);
                        } else {
                            resolve(response);
                        }
                    }
                );
            });
        },

        checkPassword(params) {
            if (
                process.env.VUE_APP_ENV === 'local' ||
                process.env.VUE_APP_ENV === 'demo'
            ) {
                return axios.post(
                    `${process.env.VUE_APP_DEMO_AUTH_API_BASE_URL}/v1/co/authenticate`,
                    params
                );
            }
            return new Promise((resolve, reject) => {
                webAuth.popup.loginWithCredentials(
                    {
                        connection: AUTH0_CONFIG.connection,
                        username: params.email,
                        password: params.password
                    },
                    (error, response) => {
                        if (error) {
                            reject(error);
                        } else {
                            this.setSession(response);
                            resolve(response);
                        }
                    }
                );
            });
        }
    }
});

export default {
    install: function(Vue) {
        Vue.prototype.auth = auth;
        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line
            console.log(store.state.user.status);
        }
    }
};
